import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  InputAdornment,
} from "@mui/material";
import {
  Close as CloseIcon,
  Search as SearchIcon,
  Person as PersonIcon,
} from "@mui/icons-material";

import { useUsers } from "../../../hooks/useAdminUsers";
import { AdminUser } from "../../../types/admin";
import { formatApiError } from "../../../utils/formatApiError";

interface AssignUserModalProps {
  open: boolean;
  cargoCompanyId: string;
  assignedUserIds: string[];
  onClose: () => void;
  onAssign: (userId: string) => Promise<void>;
  isLoading?: boolean;
  error?: any;
}

const AssignUserModal: React.FC<AssignUserModalProps> = ({
  open,
  cargoCompanyId,
  assignedUserIds,
  onClose,
  onAssign,
  isLoading = false,
  error,
}) => {
  const { t } = useTranslation();
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [submitError, setSubmitError] = useState<string>("");

  // Fetch all users
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    error: usersError,
  } = useUsers({
    page: 1,
    pageSize: 1000, // Get all users for selection
  });

  const users: AdminUser[] = usersData?.data?.users || [];

  // Filter out already assigned users and apply search filter
  const availableUsers = useMemo(() => {
    return users.filter((user) => {
      // Filter out already assigned users
      if (assignedUserIds.includes(user.id)) {
        return false;
      }

      // Apply search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          user.email.toLowerCase().includes(searchLower) ||
          user.firstName?.toLowerCase().includes(searchLower) ||
          user.lastName?.toLowerCase().includes(searchLower) ||
          user.fullName?.toLowerCase().includes(searchLower)
        );
      }

      return true;
    });
  }, [users, assignedUserIds, searchTerm]);

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setSearchTerm("");
      setSubmitError("");
    }
  }, [open]);

  useEffect(() => {
    if (error) {
      setSubmitError(formatApiError(error));
    }
  }, [error]);

  const handleAssign = async () => {
    if (!selectedUser) {
      setSubmitError(t("admin.assignUser.pleaseSelectUser"));
      return;
    }

    try {
      setSubmitError("");
      await onAssign(selectedUser.id);
      setSelectedUser(null);
      setSearchTerm("");
    } catch (err) {
      // Error handled by parent component
    }
  };

  const handleClose = () => {
    setSelectedUser(null);
    setSearchTerm("");
    setSubmitError("");
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t("admin.assignUser.title")}</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {submitError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {submitError}
          </Alert>
        )}

        {usersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {t("admin.assignUser.failedToLoadUsers")}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            placeholder={t("admin.assignUser.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {isLoadingUsers ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : availableUsers.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <PersonIcon sx={{ fontSize: 48, color: "text.secondary", mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                {searchTerm
                  ? t("admin.assignUser.noUsersFoundSearch")
                  : assignedUserIds.length === users.length
                  ? t("admin.assignUser.allUsersAssigned")
                  : t("admin.assignUser.noUsersAvailable")}
              </Typography>
            </Box>
          ) : (
            <Autocomplete
              options={availableUsers}
              getOptionLabel={(option) =>
                `${option.fullName || `${option.firstName} ${option.lastName}`} (${option.email})`
              }
              value={selectedUser}
              onChange={(_, newValue) => {
                setSelectedUser(newValue);
                setSubmitError("");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("admin.assignUser.selectUser")}
                  placeholder={t("admin.assignUser.typeToSearch")}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <ListItemAvatar>
                    <Avatar>
                      {option.firstName?.[0]}
                      {option.lastName?.[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${option.firstName} ${option.lastName}`}
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                        {option.roles && option.roles.length > 0 && (
                          <Box sx={{ mt: 0.5 }}>
                            {option.roles.slice(0, 3).map((role) => (
                              <Chip
                                key={role}
                                label={role}
                                size="small"
                                sx={{ mr: 0.5, mt: 0.5 }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </Box>
              )}
              loading={isLoadingUsers}
              noOptionsText={
                searchTerm
                  ? t("admin.assignUser.noUsersFound")
                  : t("admin.assignUser.noAvailableUsers")
              }
            />
          )}
        </Box>

        {selectedUser && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>{t("admin.assignUser.selected")}:</strong> {selectedUser.fullName || `${selectedUser.firstName} ${selectedUser.lastName}`} ({selectedUser.email})
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!selectedUser || isLoading}
          startIcon={isLoading ? <CircularProgress size={20} /> : null}
        >
          {isLoading ? t("admin.assignUser.assigning") : t("admin.assignUser.assignUser")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignUserModal;

